/**
 * PIN gate (D-018, D-133): a container asks `requestPin(message)` before a PIN-gated mutation; the
 * dialog resolves with the proof or `null` on cancel. The proof is sent **in the request body** as
 * `pin` (own) or `supervisor: { username, pin }` — exactly what apps/api/src/plugins/access.ts reads
 * (`pinProofSchema.safeParse({ pin: body.pin, supervisor: body.supervisor })`). Every B3 route is
 * `pin: "own"`, so the supervisor option is offered only where a container passes `allowSupervisor`.
 * A server-side PIN refusal (PIN_INVALID, PIN_LOCKED, …) is shown inside the dialog by `retryWith`.
 */
import { useCallback, useRef, useState, type ReactNode } from "react";
import { PinDialog } from "@faylasuf/ui";
import type { PinProof } from "../api/types.js";
import { useApp } from "./context.js";
import { errorMessage, isSessionLost } from "./errors.js";

export interface PinRequestOptions {
  readonly message?: string;
  readonly allowSupervisor?: boolean;
}

export interface PinGate {
  /** Opens the dialog; resolves with the proof, or `null` if the user cancelled. */
  readonly requestPin: (opts?: PinRequestOptions) => Promise<PinProof | null>;
  /**
   * Runs `action(proof)`; when the server refuses the PIN itself, the dialog re-opens with the
   * translated error and the action is retried with the new proof. Other errors propagate.
   * Returns `undefined` when the user cancelled.
   */
  readonly withPin: <T>(action: (proof: PinProof) => Promise<T>, opts?: PinRequestOptions) => Promise<T | undefined>;
  readonly dialog: ReactNode;
}

const PIN_CODES = new Set(["PIN_REQUIRED", "PIN_INVALID", "PIN_LOCKED", "PIN_NOT_SET", "PIN_RATE_LIMITED", "SUPERVISOR_IS_MAKER", "SUPERVISOR_LACKS_PERMISSION"]);

export function usePinGate(): PinGate {
  const { t } = useApp();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [allowSupervisor, setAllowSupervisor] = useState(false);
  const [supervisorMode, setSupervisorMode] = useState(false);
  const [pin, setPin] = useState("");
  const [supervisorUsername, setSupervisorUsername] = useState("");
  const [supervisorPin, setSupervisorPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const resolver = useRef<((proof: PinProof | null) => void) | null>(null);

  const close = useCallback((proof: PinProof | null) => {
    setOpen(false);
    setBusy(false);
    setPin("");
    setSupervisorPin("");
    const r = resolver.current;
    resolver.current = null;
    r?.(proof);
  }, []);

  const requestPin = useCallback(
    (opts: PinRequestOptions = {}) =>
      new Promise<PinProof | null>((resolve) => {
        resolver.current?.(null);
        resolver.current = resolve;
        setMessage(opts.message ?? null);
        setAllowSupervisor(opts.allowSupervisor ?? false);
        setSupervisorMode(false);
        setError(null);
        setPin("");
        setSupervisorPin("");
        setOpen(true);
      }),
    [],
  );

  const withPin = useCallback(
    async <T,>(action: (proof: PinProof) => Promise<T>, opts: PinRequestOptions = {}): Promise<T | undefined> => {
      let lastError: string | null = null;
      for (;;) {
        const proof = await new Promise<PinProof | null>((resolve) => {
          resolver.current?.(null);
          resolver.current = resolve;
          setMessage(opts.message ?? null);
          setAllowSupervisor(opts.allowSupervisor ?? false);
          if (!lastError) setSupervisorMode(false);
          setError(lastError);
          setPin("");
          setSupervisorPin("");
          setOpen(true);
        });
        if (!proof) return undefined;
        try {
          return await action(proof);
        } catch (e) {
          const code = (e as { code?: string }).code;
          if (code && PIN_CODES.has(code) && !isSessionLost(e)) {
            lastError = errorMessage(t, e);
            continue;
          }
          throw e;
        }
      }
    },
    [t],
  );

  const confirm = () => {
    setBusy(true);
    const proof: PinProof = supervisorMode ? { supervisor: { username: supervisorUsername.trim(), pin: supervisorPin } } : { pin };
    close(proof);
  };

  const dialog = (
      <PinDialog
        open={open}
        message={message}
        pin={pin}
        onPinChange={(v) => setPin(v.replace(/\D/g, "").slice(0, 6))}
        supervisorMode={supervisorMode}
        allowSupervisor={allowSupervisor}
        onSupervisorModeChange={setSupervisorMode}
        supervisorUsername={supervisorUsername}
        onSupervisorUsernameChange={setSupervisorUsername}
        supervisorPin={supervisorPin}
        onSupervisorPinChange={(v) => setSupervisorPin(v.replace(/\D/g, "").slice(0, 6))}
        onCancel={() => close(null)}
        onConfirm={confirm}
        error={error}
        busy={busy}
      />
  );

  return { requestPin, withPin, dialog };
}
